<template>
  <div>
    <!-- Server name -->
    <div class="server-info-title title">
      <span>{{ serverInfo.name }}</span>
      <span
        :title="serverInfo.active ? 'Enabled' : 'Disabled'"
        :class="{ disabled: !serverInfo.active }"
        class="server-status"
      >
        &#9679;
      </span>
    </div>

    <!-- status scaling -->
    <div class="server-info-title title">
      <span class="titleScaling">Scaling status</span>
      <span
        :title="statusScaling ? this.scalingMessage : 'No scaling required'"
        class="server-status"
        :class="{ disabled: statusScaling }"
      >
        &#9679;
      </span>
    </div>

    <!-- Manager URL -->
    <!-- <div class="info-block info-block__wrapper">
      <span class="subtitle">Scaling</span>
      <div class="info-data marginScaling">
        <span v-if="serverInfo.active" class="enabled">Need</span>
        <span v-else class="disabled">Don't have to</span>
      </div>
    </div> -->

    <!-- Manager URL -->
    <div class="info-block info-block__wrapper">
      <span class="subtitle">WSE Manager</span>
      <div class="info-data">
        <span>
          <a :href="serverInfo.managerUrl" target="_blank">
            {{ serverInfo.managerUrl }}
          </a>
        </span>
      </div>
    </div>

    <!-- Server interface URL -->
    <div class="info-block info-block__wrapper">
      <span class="subtitle">API URL</span>
      <div class="info-data">
        <span>
          <a :href="serverInfo.apiUrl" target="_blank">
            {{ serverInfo.apiUrl }}
          </a>
        </span>
      </div>
    </div>

    <!-- Hidden for disabled servers -->
    <template v-if="serverInfo.active">
      <!-- Server working time -->
      <div class="info-block info-block__wrapper">
        <span class="subtitle">Uptime</span>
        <div class="info-data">
          {{ uptimeLine }}
        </div>
      </div>

      <!-- RAM usage statistics -->
      <div class="info-block info-block__wrapper">
        <span class="subtitle">Memory usage</span>
        <div class="info-data">
          <memory-usage-bar
            :full-memory="serverInfo.fullMemory"
            :used-memory="serverInfo.memoryUsage"
            class="info-usage-bar"
          />
        </div>
      </div>

      <!-- CPU usage statistics -->
      <div class="info-block info-block__wrapper">
        <span class="subtitle">CPU usage</span>
        <div class="info-data">
          <percentage-usage-bar
            class="info-usage-bar"
            :usage="serverInfo.cpuUsage"
          />
        </div>
      </div>

      <!-- Disk usage statistics -->
      <div class="info-block info-block__wrapper">
        <span class="subtitle">Disk usage</span>
        <div class="info-data">
          <memory-usage-bar
            :full-memory="serverInfo.fullDisk"
            :used-memory="serverInfo.diskUsage"
            class="info-usage-bar"
          />
        </div>
      </div>

      <!-- Disk util -->
      <div class="info-block info-block__wrapper">
        <span class="subtitle">Disk util</span>
        <div class="info-data">
          <disk-util
            :full-memory="100"
            :used-memory="serverInfo.data.diskUtil"
            class="info-usage-bar"
          />
        </div>
      </div>

      <!-- Iframe fraphic -->
      <div class="info-block info-block__wrapper">
        <iframe-admin-bar :server-info="serverInfo" />
      </div>

      <!-- Server streams list -->
      <div class="info-block info-block__wrapper">
        <admin-streams-list
          :streams="serverInfo.streams"
          @updateServerInfoFlag="$emit('updateServerInfoFlag', $event)"
          @streamAddingStarted="showAddStreamModal"
        />
      </div>
      <!-- Server streams list -->
      <div class="info-block info-block__wrapper">
        <admin-log-list />
      </div>
    </template>
  </div>
</template>

<script>
import AdminStreamsList from "@/components/admin/main/server/AdminStreamsList.vue";
import AdminLogList from "@/components/admin/main/server/AdminLogList.vue";
import PercentageUsageBar from "@/components/admin/bars/PercentageUsageBar.vue";
import MemoryUsageBar from "@/components/admin/bars/MemoryUsageBar.vue";
import IframeAdminBar from "@/components/admin/bars/IframeAdminBar.vue";
import DiskUtil from "@/components/admin/bars/DiskUtil.vue";
import { getWorkerScaling } from "@/api/auth.js";

/** Vuex methods */
import { mapActions, mapState } from "vuex";

// @group Admin Page
// Detailed server information panel
export default {
  name: "AdminServerInfo",
  components: {
    AdminStreamsList,
    PercentageUsageBar,
    MemoryUsageBar,
    IframeAdminBar,
    DiskUtil,
    AdminLogList
  },
  props: {
    // server information
    serverInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      /** lazy uptime (smooth updatin) */
      uptime: this.serverInfo.uptime,
      /** lazy uptime updating interval */
      uptimeUpdateDuration: 500,
      statusScaling: true,
      scalingMessage: ""
    };
  },
  async created() {
    this.checkScaling();
  },
  computed: {
    ...mapState({ modals: state => state.modals.admin.MODALS }),
    serverId() {
      return this.serverInfo.id;
    },
    uptimeLine() {
      let uptime = this.uptime;

      /** days */
      const d = Math.floor(uptime / (3600 * 24));
      uptime -= d * 24 * 3600;

      /** hours */
      const h = Math.floor(uptime / 3600);
      uptime -= h * 3600;

      /** minutes */
      const m = Math.floor(uptime / 60);
      uptime -= m * 60;

      /** seconds */
      const s = uptime;

      /** time in format DD:HH:MM:SS */
      const uptimeLine = [d, h, m, s]
        .map(t => t.toString().padStart(2, "0"))
        .join(":");

      return uptimeLine;
    }
  },
  watch: {
    async serverId() {
      this.checkScaling();
    },
    // @vuese
    // Smooth uptime updating
    "serverInfo.uptime"() {
      this.increaseUptimeRecursively();
    }
  },
  methods: {
    //scalingMessage
    ...mapActions("modals", ["openAdminModal"]),
    async checkScaling() {
      const resp = await getWorkerScaling();
      console.log("RSP scaling status", resp);
      const check = resp.filter(e => {
        return e.workerId === this.serverInfo.id;
      })[0];
      this.statusScaling = check.shouldScale;
      this.scalingMessage = check.message;
    },
    // @vuese
    // Show add stream modal
    showAddStreamModal() {
      this.openAdminModal({
        modal: this.modals.ADD,
        data: {
          appName: this.serverInfo.ndvrApp,
          serverName: this.serverInfo.name,
          serverId: this.serverInfo.id
        }
      });
    },
    // @vuese
    // Update uptime slowly
    increaseUptimeRecursively() {
      const difference = Math.abs(
        Math.round(this.serverInfo.uptime - this.uptime)
      );
      const direction = Math.sign(this.serverInfo.uptime - this.uptime);

      if (difference) {
        const timeStep = Math.max(this.uptimeUpdateDuration / difference, 1);
        this.uptime += 1 * direction;
        setTimeout(this.increaseUptimeRecursively, timeStep);
      }
    }
  }
};
</script>

<style scoped>
.titleScaling {
  font-size: 17px;
  font-weight: 600;
}
.enabled {
  color: var(--success-color);
}

.marginScaling {
  margin-top: 10px;
}

.server-info-title {
  display: flex;
  align-items: center;
  padding-bottom: 0.5em;
}

.server-status {
  padding-left: 5px;
  padding-bottom: 4px;
  font-size: 2rem;

  color: var(--success-color);
}

.server-status.disabled {
  color: var(--error-color);
}

.info-block {
  display: flex;
  flex-direction: column;
}
.info-block__wrapper {
  padding-bottom: 1.5em;
}

.info-usage-bar {
  height: 50px;
  width: 100%;
}
</style>
